import { Directive, ElementRef, Input, Renderer2, OnInit, OnChanges, SimpleChanges } from '@angular/core';

/**
 * [vbImageLoad]
 *
 * Angular directive for handling image loading with a placeholder and fade-in effect.
 * - Applies a background color placeholder while the image loads.
 * - Fades in the image once loaded, removing the placeholder.
 * - Supports both background images (via [bgImageUri]) and <img> tags.
 * - Handles image load and error events for graceful fallback.
 *
 * Usage:
 *   <div vbImageLoad [bgImageUri]="imageUrl"></div>
 *   <img vbImageLoad [src]="imageUrl">
 *
 * Synced with VbImageLoad.Directive.ts in 'rev-avenger-core' repo but with a different placeholder color
 */
@Directive({
	selector: '[vbImageLoad]',
	standalone: true
})
export class VbImageLoadDirective implements OnInit, OnChanges {
	@Input() public bgImageUri: string;

	private readonly defaultPlaceholderColorStyle = '#000000 !important'; // Black placeholder color for player
	private readonly transitionStyle = 'opacity 0.3s ease-in-out';
	private readonly transitionDuration = 300; // matches opacity transition duration

	constructor(
		private readonly el: ElementRef,
		private readonly renderer: Renderer2
	) { }

	public ngOnInit(): void {
		if (!this.hasImageToLoad()) {
			return;
		}

		this.renderer.setStyle(this.el.nativeElement, 'background-color', this.defaultPlaceholderColorStyle);
		this.renderer.setStyle(this.el.nativeElement, 'opacity', '0');
		this.renderer.setStyle(this.el.nativeElement, 'transition', this.transitionStyle);

		if (this.bgImageUri) {
			this.preloadBackgroundImage(this.bgImageUri);
		} else if (this.el.nativeElement?.tagName.toLowerCase() === 'img') {
			this.renderer.listen(this.el.nativeElement, 'load', () => {
				this.renderer.setStyle(this.el.nativeElement, 'opacity', '1');
				setTimeout(() => {
					this.renderer.removeStyle(this.el.nativeElement, 'background-color');
				}, this.transitionDuration);
			});
			this.renderer.listen(this.el.nativeElement, 'error', () => {
				this.renderer.setStyle(this.el.nativeElement, 'background-color', this.defaultPlaceholderColorStyle);
				this.renderer.setStyle(this.el.nativeElement, 'opacity', '1');
			});
		}
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if (changes.bgImageUri && this.hasImageToLoad()) {
			this.renderer.setStyle(this.el.nativeElement, 'background-image', `url(${this.bgImageUri})`);
		}
	}

	private hasImageToLoad(): boolean {
		return (this.bgImageUri && this.bgImageUri.trim() !== '') || this.el.nativeElement?.tagName.toLowerCase() === 'img';
	}

	private preloadBackgroundImage(url: string): void {
		const img = new Image();
		img.onload = () => {
			this.renderer.setStyle(this.el.nativeElement, 'background-image', `url(${this.bgImageUri})`);
			this.renderer.setStyle(this.el.nativeElement, 'opacity', '1');
			setTimeout(() => {
				this.renderer.removeStyle(this.el.nativeElement, 'background-color');
			}, this.transitionDuration);
			img.onload = null;
			img.onerror = null;
		};
		img.onerror = () => {
			img.onload = null;
			img.onerror = null;
		};
		img.src = url;
	}
}
